import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import { LogIn, AlertCircle, Shield, BarChart3 } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { useBrand } from '../providers/BrandProvider';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const brand = useBrand();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.login({ email, password });

      if (response.success && response.data) {
        const { token, user, organization } = response.data;
        setAuth(token, user, organization);
        navigate('/');
      } else {
        setError(response.error?.message || t('auth.loginError'));
      }
    } catch (err: any) {
      setError(err?.error?.message || t('auth.loginErrorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-surface-50">
      {/* Left side — Brand panel */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden">
        <div className="absolute inset-0" style={{
          background: `linear-gradient(135deg, ${brand.loginGradient[0]} 0%, ${brand.loginGradient[1]} 100%)`
        }} />
        <div className="absolute inset-0">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-surface-0/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-[400px] h-[400px] bg-white/5 rounded-full blur-3xl" />
          <div className="absolute top-1/3 left-1/2 w-64 h-64 bg-white/5 rounded-full blur-2xl" />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div>
            <div className="mb-6">
              <BrandLogo variant="login-dark" />
            </div>
            <h1 className="text-4xl font-bold font-display font-headline tracking-tight">{brand.productName}</h1>
            <p className="text-white/70 mt-2 text-lg">{brand.loginSubtitle}</p>
          </div>

          <div className="space-y-6">
            <div className="flex items-start gap-4 p-4 rounded-xl"
                 style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                   style={{ background: 'rgba(255,255,255,0.12)' }}>
                <Shield size={20} className="text-white/80" />
              </div>
              <div>
                <p className="font-medium font-headline">{t('auth.feature1Title', 'ניהול פרויקטים חכם')}</p>
                <p className="text-sm text-white/60 mt-0.5">{t('auth.feature1Desc', 'נהל משימות, זמנים ותקציבים במקום אחד')}</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl"
                 style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                   style={{ background: 'rgba(255,255,255,0.12)' }}>
                <BarChart3 size={20} className="text-white/80" />
              </div>
              <div>
                <p className="font-medium font-headline">{t('auth.feature2Title', 'דוחות ותובנות')}</p>
                <p className="text-sm text-white/60 mt-0.5">{t('auth.feature2Desc', 'BI מתקדם עם SLA, נוכחות ובילינג')}</p>
              </div>
            </div>
          </div>

          <p className="text-xs text-white/40 font-headline">{brand.copyrightText}</p>
        </div>
      </div>

      {/* Right side — login form on warm surface */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-10">
            <div className="mx-auto mb-4 flex justify-center">
              <BrandLogo variant="login" />
            </div>
            <h1 className="text-2xl font-bold font-display font-headline text-surface-900 tracking-tight">{brand.productName}</h1>
            <p className="text-surface-500 text-sm mt-1">{brand.loginSubtitle}</p>
          </div>

          <div className="mb-8 hidden lg:block">
            <h2 className="text-2xl font-bold font-headline text-surface-900 tracking-tight">
              {t('auth.loginTitle')}
            </h2>
            <p className="text-surface-600 mt-1.5">{t('auth.loginSubtitle', 'הכנס את פרטי ההתחברות שלך')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="label">
                {t('common.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder={t('auth.emailPlaceholder', 'your@email.com')}
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                {t('common.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div className="info-card-error text-danger-700 text-sm animate-fade-in">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              className="w-full btn btn-primary btn-lg shadow-card hover:shadow-glow transition-all inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:pointer-events-none"
            >
              <LogIn size={20} className={loading ? 'opacity-50' : ''} />
              <span>{loading ? t('common.loading') : t('auth.login')}</span>
            </button>
          </form>

          <p className="text-center text-xs text-surface-500 mt-10">
            {brand.copyrightText}
          </p>
        </div>
      </div>
    </div>
  );
}
